import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import { AppDispatch } from '../../store/store';
import { getInsights } from '../../rtk-apis/dashboard/getInsights';
import {
  updateClinicBilling,
  type BillingBasis,
} from '../../rtk-apis/dashboard/updateClinicBilling';
import { sendInvoice } from '../../rtk-apis/dashboard/sendInvoice';
import { generateInvoices, previewInvoiceRange, type InvoicePreview } from '../../rtk-apis/dashboard/billing';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { countStudiesByAeTitle } from '../../Utils/pacsStudyCounts';

/* ════════════════════════════════════════════════════════════════════════
   Clinics & rates — what each clinic is charged, and raising an invoice.

   Moved here from the Super Admin dashboard. Setting a price, scheduling the
   billing run and raising an invoice are all money jobs; having them sit under
   the performance charts meant the person doing the billing had to scroll past
   analytics that were irrelevant to the task.
   ════════════════════════════════════════════════════════════════════════ */

/**
 * Studies counted by the imaging archive, keyed by AE title.
 *
 * The `studies` figure the API returns is derived from the conversion pipeline, which is
 * known to under-report — it only records a study that also converted, uploaded and had
 * its filename parsed, and its writer overwrites concurrent updates. The archive knows
 * what arrived. Since per-study billing is calculated from this number, showing the
 * pipeline's version here means quietly under-billing.
 */
const money = (n: number, currency = 'AUD') => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString()}`;
  }
};

const cardSx = {
  background: `linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))`,
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  p: '18px 20px',
  minWidth: 0,
};

export interface ClinicRow {
  clinicId: string;
  name: string;
  reports: number;
  echo: number;
  stress: number;
  reportedStudies: number;
  studies: number;
  studiesApprox: boolean;
  aeTitles: string[];
  billing: {
    basis: BillingBasis;
    rate: number;
    currency: string;
    paymentTermsDays?: number;
    paymentTermsMode?: 'days' | 'end_of_month';
    abn?: string;
    billingEmail?: string;
    autoInvoice?: {
      enabled?: boolean;
      frequency?: 'weekly' | 'fortnightly' | 'monthly';
      anchorDay?: number;
      autoSend?: boolean;
      nextRunOn?: string;
    };
  };
  billableCount: number;
  revenue: number;
  prevReports: number;
  reportsDelta: number | null;
  avgTatHours: number | null;
  statusMix: Record<string, number>;
  unreported: number;
}

interface Props {
  year: number;
  month: number | 'all';
  onChanged?: () => void;
  notify?: (msg: string, sev: 'success' | 'error') => void;
}

const ClinicRatesTable: React.FC<Props> = ({ year, month, onChanged, notify }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [rows, setRows] = useState<ClinicRow[]>([]);
  const [totals, setTotals] = useState<{ currency?: string; unconfiguredCount?: number }>({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ClinicRow | null>(null);
  const [draftBasis, setDraftBasis] = useState<BillingBasis>('per_report');
  const [draftRate, setDraftRate] = useState('0');
  const [draftTerms, setDraftTerms] = useState('14');
  const [draftTermsMode, setDraftTermsMode] = useState<'days' | 'end_of_month'>('days');
  const [draftAbn, setDraftAbn] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftAuto, setDraftAuto] = useState(false);
  const [draftFreq, setDraftFreq] = useState<'weekly' | 'fortnightly' | 'monthly'>('monthly');
  const [draftAnchor, setDraftAnchor] = useState('1');
  const [draftAutoSend, setDraftAutoSend] = useState(false);
  const [invoicingId, setInvoicingId] = useState<string | null>(null);
  // On-demand, arbitrary-range invoice generation scoped to ONE clinic — Nikhil, 22 Sep:
  // "can we have finer control? specific clinics choosing. we dont want invoice for all
  // clinics in the future. keep this in clinics and rates page maybe."
  const [rangeFor, setRangeFor] = useState<ClinicRow | null>(null);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [generatingRange, setGeneratingRange] = useState(false);
  // Two-step flow — Kevin, 22 Sep: "after clicking generate show basic data about
  // subtotal and all, then below button send mail and then generate." Previewing computes
  // the numbers without writing anything; only "Save as draft" / "Generate & send" persist.
  const [previewing, setPreviewing] = useState(false);
  const [rangePreview, setRangePreview] = useState<InvoicePreview | null>(null);

  const currency = totals.currency || 'AUD';

  /**
   * Archive counts per AE title for the selected period. Null for a title the archive
   * could not answer, so that clinic alone falls back to the API figure instead of being
   * shown as zero.
   */
  const [pacsByAet, setPacsByAet] = useState<Record<string, number | null>>({});

  useEffect(() => {
    const aeTitles = rows.flatMap(r => r.aeTitles || []);
    if (!aeTitles.length) {
      setPacsByAet({});
      return undefined;
    }
    // 'all' means the whole calendar year; a numeric month means that month alone.
    const periodStart = month === 'all' ? new Date(year, 0, 1) : new Date(year, month - 1, 1);
    const periodEnd =
      month === 'all'
        ? new Date(year, 11, 31, 23, 59, 59, 999)
        : new Date(year, month, 0, 23, 59, 59, 999);

    let cancelled = false;
    countStudiesByAeTitle(aeTitles, periodStart, periodEnd)
      .then(counts => {
        if (!cancelled) {
          setPacsByAet(counts);
        }
      })
      .catch(err => {
        console.warn('[billing] archive study counts unavailable:', err);
        if (!cancelled) {
          setPacsByAet({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rows, year, month]);

  /**
   * A clinic's studies as the archive counts them: the sum over its AE titles. Returns
   * null when NO title could be answered, so the caller keeps the API figure rather than
   * inventing a zero. Where a title is shared between clinics the archive cannot split it
   * either, so those rows stay flagged approximate exactly as before.
   */
  const archiveStudiesFor = (row: ClinicRow): number | null => {
    const titles = row.aeTitles || [];
    const known = titles
      .map(t => pacsByAet[(t || '').trim()])
      .filter((n): n is number => typeof n === 'number');
    return known.length ? known.reduce((a, b) => a + b, 0) : null;
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = (await dispatch(getInsights({ year, month })).unwrap()) as {
        status?: string;
        data?: { clinics: ClinicRow[]; totals: { currency?: string; unconfiguredCount?: number } };
      };
      if (res?.status === 'SUCCESS' && res.data) {
        setRows(res.data.clinics || []);
        setTotals(res.data.totals || {});
      }
    } catch (err) {
      console.error('[billing] clinic rates fetch failed:', err);
    } finally {
      setBusy(false);
    }
  }, [dispatch, year, month]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Plain-English read-back of the chosen schedule, including the resulting due date.
   * Frequency, anchor day and payment terms interact, and stating the outcome removes
   * the guesswork about which period gets billed and when payment is expected.
   */
  const scheduleSummary = (() => {
    const days = Number(draftTerms) || 0;
    const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][
      (Number(draftAnchor) || 1) - 1
    ];
    const nth = (n: number) => {
      const s =
        ['th', 'st', 'nd', 'rd'][((n % 100) - 20) % 10] ||
        ['th', 'st', 'nd', 'rd'][n % 100] ||
        'th';
      return `${n}${s}`;
    };
    const action = draftAutoSend ? 'emailed to the clinic' : 'created as a draft for you to review';
    // The read-back has to follow the due-date mode, or it keeps promising "14 days
    // later" while the invoice actually falls due at month end — the summary exists
    // precisely so nobody has to work the interaction out for themselves.
    const dueSentence =
      draftTermsMode === 'end_of_month'
        ? 'Payment is due at the end of the month it is raised in.'
        : `Payment is due ${days} days later.`;
    if (draftFreq === 'monthly') {
      return `On the ${nth(Number(draftAnchor) || 1)} of each month, an invoice covering the whole of the previous month is ${action}. ${dueSentence}`;
    }
    if (draftFreq === 'fortnightly') {
      return `Every second ${dayName}, an invoice covering the previous 14 days is ${action}. ${dueSentence}`;
    }
    return `Every ${dayName}, an invoice covering the previous 7 days is ${action}. ${dueSentence}`;
  })();

  const openEditor = (row: ClinicRow) => {
    setEditing(row);
    setDraftBasis(row.billing?.basis || 'per_report');
    setDraftRate(String(row.billing?.rate ?? 0));
    setDraftTerms(String(row.billing?.paymentTermsDays ?? 14));
    setDraftTermsMode(row.billing?.paymentTermsMode || 'days');
    setDraftAbn(row.billing?.abn || '');
    setDraftEmail(row.billing?.billingEmail || '');
    setDraftAuto(!!row.billing?.autoInvoice?.enabled);
    setDraftFreq(row.billing?.autoInvoice?.frequency || 'monthly');
    setDraftAnchor(String(row.billing?.autoInvoice?.anchorDay ?? 1));
    setDraftAutoSend(!!row.billing?.autoInvoice?.autoSend);
  };

  const saveBilling = async () => {
    if (!editing) {
      return;
    }
    const rate = Number(draftRate);
    if (!Number.isFinite(rate) || rate < 0) {
      return;
    }
    try {
      await dispatch(
        updateClinicBilling({
          clinicId: editing.clinicId,
          basis: draftBasis,
          rate,
          currency,
          paymentTermsDays: Number(draftTerms) || 14,
          paymentTermsMode: draftTermsMode,
          abn: draftAbn.trim(),
          billingEmail: draftEmail.trim(),
          autoInvoice: {
            enabled: draftAuto,
            frequency: draftFreq,
            anchorDay: Number(draftAnchor) || 1,
            autoSend: draftAutoSend,
          },
        })
      ).unwrap();
      setEditing(null);
      notify?.('Billing settings saved.', 'success');
      load();
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not save the billing settings.', 'error');
    }
  };

  const emailInvoice = async (row: ClinicRow) => {
    setInvoicingId(row.clinicId);
    try {
      const res = (await dispatch(
        sendInvoice({ clinicId: row.clinicId, year, month })
      ).unwrap()) as { msg?: string };
      notify?.(res?.msg || 'Invoice sent.', 'success');
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Failed to send invoice.', 'error');
    } finally {
      setInvoicingId(null);
    }
  };

  const openRangeGenerator = (row: ClinicRow) => {
    setRangeFor(row);
    setRangeFrom('');
    setRangeTo('');
    setRangePreview(null);
  };

  /** Step 1: compute the numbers. Nothing is written or sent yet. */
  const submitRangePreview = async () => {
    if (!rangeFor || !rangeFrom || !rangeTo) {
      return;
    }
    setPreviewing(true);
    setRangePreview(null);
    try {
      const res = (await dispatch(
        previewInvoiceRange({ clinicId: rangeFor.clinicId, from: rangeFrom, to: rangeTo })
      ).unwrap()) as { data: InvoicePreview };
      setRangePreview(res.data);
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not compute the preview.', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  /** Step 2: the person has seen the numbers and picked what happens with them. */
  const confirmRangeGenerate = async (send: boolean) => {
    if (!rangeFor || !rangeFrom || !rangeTo) {
      return;
    }
    setGeneratingRange(true);
    try {
      const res = (await dispatch(
        generateInvoices({ from: rangeFrom, to: rangeTo, clinicIds: [rangeFor.clinicId], send })
      ).unwrap()) as { msg?: string };
      notify?.(res?.msg || (send ? 'Invoice generated and sent.' : 'Invoice saved as draft.'), 'success');
      setRangeFor(null);
      setRangePreview(null);
      load();
      onChanged?.();
    } catch (err) {
      notify?.(typeof err === 'string' ? err : 'Could not generate the invoice.', 'error');
    } finally {
      setGeneratingRange(false);
    }
  };

  return (
    <Box>
      {/* per-clinic table */}
      <Box sx={{ ...cardSx, p: '18px 20px 8px' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '12px' }}>
          Clinic performance
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 940 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.7fr 0.75fr 0.8fr 0.85fr 0.7fr 1.05fr 0.95fr 0.8fr',
                gap: '12px',
                pb: '8px',
                borderBottom: `1px solid ${T.line}`,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.ink3,
              }}
            >
              <span>Clinic</span>
              <span>Reports</span>
              <span>Echo / Stress</span>
              <span>Studies</span>
              <span>Turnaround</span>
              <span>Rate</span>
              <span>Revenue</span>
              <span style={{ textAlign: 'right' }}>Actions</span>
            </Box>
            {rows.map(r => (
              <Box
                key={r.clinicId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.7fr 0.75fr 0.8fr 0.85fr 0.7fr 1.05fr 0.95fr 0.8fr',
                  gap: '12px',
                  alignItems: 'center',
                  py: '11px',
                  borderBottom: `1px solid ${T.line}`,
                  fontSize: 13.5,
                  color: T.ink,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.name}
                  </Typography>
                  {r.studiesApprox && (
                    <Tooltip
                      title={`AE title ${r.aeTitles.join(', ')} is shared with another clinic, so studies cannot be split between them.`}
                    >
                      <Chip
                        size="small"
                        label="AE shared"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          mt: '3px',
                          color: T.amber,
                          background: 'rgba(247,163,90,0.12)',
                          border: `1px solid rgba(247,163,90,0.4)`,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {r.reports}
                  {r.reportsDelta != null && r.reportsDelta !== 0 && (
                    <Tooltip title={`vs previous period (${r.prevReports})`}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 11,
                          color: r.reportsDelta > 0 ? T.green : T.red,
                        }}
                      >
                        {r.reportsDelta > 0 ? (
                          <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                        )}
                        {Math.abs(r.reportsDelta)}%
                      </Box>
                    </Tooltip>
                  )}
                </Box>
                <span style={{ color: T.ink2 }}>
                  {r.echo} / {r.stress}
                </span>
                {(() => {
                  const archived = archiveStudiesFor(r);
                  const shown = archived ?? r.studies;
                  // The API figure is what per-study invoices were calculated from. If the
                  // archive counted more, the difference is money not billed, so it is
                  // called out rather than silently replaced.
                  const undercount = archived != null && archived > r.studies;
                  return (
                    <Tooltip
                      title={
                        undercount
                          ? `The imaging archive counted ${archived}; billing used ${r.studies}. ` +
                            'Invoices raised for this period under-count by the difference.'
                          : `${r.reportedStudies} of these produced a report` +
                            (archived != null ? ' · counted by the imaging archive' : '')
                      }
                    >
                      <span style={{ color: undercount ? T.amber : T.ink2 }}>
                        {shown}
                        {r.studiesApprox ? '*' : ''}
                        {undercount ? ' \u26a0' : ''}
                      </span>
                    </Tooltip>
                  );
                })()}
                <Tooltip
                  title={
                    r.avgTatHours != null
                      ? 'Average hours from report creation to finalise'
                      : 'No finalised reports in this period'
                  }
                >
                  <span style={{ color: T.ink2 }}>
                    {r.avgTatHours != null ? `${r.avgTatHours} h` : '—'}
                  </span>
                </Tooltip>
                <span style={{ color: r.billing.rate ? T.ink2 : T.amber }}>
                  {r.billing.rate
                    ? r.billing.basis === 'monthly'
                      ? `${money(r.billing.rate, r.billing.currency)} / month`
                      : `${money(r.billing.rate, r.billing.currency)} / ${r.billing.basis === 'per_study' ? 'study' : 'report'}`
                    : 'Not set'}
                </span>
                <span style={{ fontWeight: 700, color: r.revenue ? '#fff' : T.ink3 }}>
                  {r.revenue ? money(r.revenue, r.billing.currency) : '—'}
                </span>
                <Box sx={{ display: 'flex', gap: '2px', justifySelf: 'end' }}>
                  <IconButton
                    size="small"
                    onClick={() => openEditor(r)}
                    title="Set billing rate"
                    sx={{ color: T.ink3 }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Tooltip
                    title={
                      r.billing.rate
                        ? `Email invoice (${money(r.revenue, r.billing.currency)})`
                        : 'Set a rate before invoicing'
                    }
                  >
                    {/* span wrapper: a disabled IconButton fires no events, so MUI has
                        nothing to anchor the tooltip to without one. */}
                    <span>
                      <IconButton
                        size="small"
                        disabled={!r.billing.rate || invoicingId === r.clinicId}
                        onClick={() => emailInvoice(r)}
                        sx={{ color: r.billing.rate ? T.teal : T.ink3 }}
                      >
                        {invoicingId === r.clinicId ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: T.teal }}
                          />
                        ) : (
                          <ReceiptLongOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={r.billing.rate ? 'Generate invoice for a custom date range' : 'Set a rate before invoicing'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!r.billing.rate}
                        onClick={() => openRangeGenerator(r)}
                        sx={{ color: r.billing.rate ? T.ink2 : T.ink3 }}
                      >
                        <DateRangeOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
            {!rows.length && !busy && (
              <Typography sx={{ fontSize: 13, color: T.ink3, py: 3, textAlign: 'center' }}>
                No clinics found.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {/* rate editor */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>
          Billing rate — {editing?.name}
        </DialogTitle>
        {/* Two columns: pricing on the left, recurring schedule on the right. As a
            single column this ran well past the height of a laptop screen and had to be
            scrolled to reach Save, which hides the effect of the settings above it. */}
        <DialogContent
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 2,
            alignItems: 'start',
            pt: '8px !important',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: T.ink3, mb: '6px' }}>Charge basis</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={draftBasis}
                onChange={(_, v) => {
                  if (!v) {
                    return;
                  }
                  setDraftBasis(v);
                  // Switching an existing weekly/fortnightly clinic onto a subscription
                  // would otherwise leave a combination the server rejects, and the save
                  // would fail with an error about a field the user never touched.
                  if (v === 'monthly') {
                    setDraftFreq('monthly');
                  }
                }}
              >
                <ToggleButton value="per_report">Per report</ToggleButton>
                <ToggleButton value="per_study">Per study</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              // The rate means something different for a subscription — it is the whole
              // fee, not a unit price — so the label says so rather than leaving someone
              // to enter a per-report number against a flat charge.
              label={draftBasis === 'monthly' ? `Monthly fee (${currency})` : `Rate (${currency})`}
              size="small"
              type="number"
              value={draftRate}
              onChange={e => setDraftRate(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              helperText={
                draftBasis === 'monthly'
                  ? 'Charged in full each month regardless of how many studies or reports the clinic sends.'
                  : undefined
              }
            />
            {draftBasis === 'per_study' && editing?.studiesApprox && (
              <Typography sx={{ fontSize: 12, color: T.amber }}>
                This clinic shares an AE title with another, so its study count cannot be separated.
                Per-study billing here will over-count. Per report is exact.
              </Typography>
            )}

            {/* The address invoices will go to once live sending is enabled. Until it
                is set, mailSafety has no intended recipient to fall back to, so this is
                the real prerequisite for going live — not the env flag. */}
            <TextField
              label="Billing email"
              size="small"
              type="email"
              value={draftEmail}
              onChange={e => setDraftEmail(e.target.value)}
              placeholder="accounts@clinic.com.au"
              helperText="Where this clinic's invoices go once live sending is on. Nothing reaches it while billing is in test mode."
            />

            {/* Printed under "Billed to" on the invoice. Free text: an ACN is 9 digits
                where an ABN is 11, and some practices bill through a parent entity —
                rejecting what the clinic supplied would leave the invoice with no
                identifier at all. */}
            <TextField
              label="Clinic ABN / ACN"
              size="small"
              value={draftAbn}
              onChange={e => setDraftAbn(e.target.value)}
              placeholder="51 824 753 556"
              helperText="Shown under the clinic's name on every invoice. Leave blank if you do not have it — the line is omitted rather than left empty."
            />

            <Box>
              <Typography sx={{ fontSize: 12, color: T.ink3, mb: '6px' }}>Due date</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draftTermsMode}
                onChange={(_, v) => v && setDraftTermsMode(v)}
                sx={{ mb: '10px' }}
              >
                <ToggleButton value="days">Fixed days</ToggleButton>
                <ToggleButton value="end_of_month">End of month</ToggleButton>
              </ToggleButtonGroup>
              {draftTermsMode === 'days' ? (
                <TextField
                  label="Payment terms (days)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draftTerms}
                  onChange={e => setDraftTerms(e.target.value)}
                  inputProps={{ min: 0, max: 180 }}
                  helperText={`Days from the invoice date to its due date. At ${
                    Number(draftTerms) || 0
                  } days, an invoice raised today would be due ${new Date(
                    Date.now() + (Number(draftTerms) || 0) * 86400000
                  ).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                  })}. Independent of how often you invoice.`}
                />
              ) : (
                <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                  {/* Anchored to the month the invoice is RAISED in, not the month billed:
                      a July invoice raised on 2 August falls due 31 August, which is the
                      month the clinic can actually pay it in. */}
                  Due on the last day of the month the invoice is raised in. An invoice raised today
                  would be due{' '}
                  {new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    0
                  ).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  . Practices reconcile monthly, so this keeps one clinic&apos;s invoices inside a
                  single payment run.
                </Typography>
              )}
            </Box>
          </Box>

          {/* Recurring billing. The job only ever invoices a period that has already
              closed, so enabling this cannot raise a bill for a month still running. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              // On a narrow screen the columns stack, so the divider returns to the top
              // edge; side by side it separates the two halves vertically.
              borderTop: { xs: `1px solid ${T.line}`, md: 'none' },
              borderLeft: { xs: 'none', md: `1px solid ${T.line}` },
              pt: { xs: 2, md: 0 },
              pl: { xs: 0, md: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                Automatic invoicing
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={draftAuto ? 'on' : 'off'}
                onChange={(_, v) => v && setDraftAuto(v === 'on')}
              >
                <ToggleButton value="off">Off</ToggleButton>
                <ToggleButton value="on">On</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {draftAuto && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Select
                  size="small"
                  value={draftFreq}
                  onChange={e =>
                    setDraftFreq(e.target.value as 'weekly' | 'fortnightly' | 'monthly')
                  }
                >
                  {/* Each option explains the period it covers, because "fortnightly"
                      and "raise on day X" are not self-evident and getting them wrong
                      bills a clinic for the wrong window. */}
                  <MenuItem value="monthly">
                    <Tooltip
                      placement="right"
                      title="One invoice per calendar month, covering the whole of the month that has just ended. Raised on the day you choose in the following month."
                    >
                      <span>Monthly — one invoice per calendar month</span>
                    </Tooltip>
                  </MenuItem>
                  {/* A monthly subscription billed fortnightly or weekly would charge
                      the full monthly fee 2-4 times a month. The server rejects that
                      combination outright; these are disabled so it cannot be built in
                      the first place rather than failing on save. */}
                  <MenuItem
                    value="fortnightly"
                    disabled={draftBasis === 'monthly'}
                  >
                    <Tooltip
                      placement="right"
                      title={
                        draftBasis === 'monthly'
                          ? 'Not available for a monthly subscription — it would charge the full monthly fee twice a month.'
                          : 'One invoice every two weeks, covering the 14 days immediately before it is raised. Raised on the weekday you choose, every second week.'
                      }
                    >
                      <span>Fortnightly — every 2 weeks, covers 14 days</span>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem
                    value="weekly"
                    disabled={draftBasis === 'monthly'}
                  >
                    <Tooltip
                      placement="right"
                      title={
                        draftBasis === 'monthly'
                          ? 'Not available for a monthly subscription — it would charge the full monthly fee four times a month.'
                          : 'One invoice every week, covering the 7 days immediately before it is raised. Raised on the weekday you choose.'
                      }
                    >
                      <span>Weekly — every week, covers 7 days</span>
                    </Tooltip>
                  </MenuItem>
                </Select>
                {draftFreq === 'monthly' ? (
                  <TextField
                    label="Raise on day of month"
                    size="small"
                    type="number"
                    value={draftAnchor}
                    onChange={e => setDraftAnchor(e.target.value)}
                    inputProps={{ min: 1, max: 31 }}
                    helperText="29–31 fall back to the last day in shorter months."
                  />
                ) : (
                  <Select
                    size="small"
                    value={draftAnchor}
                    onChange={e => setDraftAnchor(String(e.target.value))}
                  >
                    {[
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ].map((d, i) => (
                      <MenuItem
                        key={d}
                        value={String(i + 1)}
                      >
                        Raise on {d}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography sx={{ fontSize: 12.5, color: T.ink2 }}>
                    Email it automatically
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={draftAutoSend ? 'send' : 'draft'}
                    onChange={(_, v) => v && setDraftAutoSend(v === 'send')}
                  >
                    <ToggleButton value="draft">Draft only</ToggleButton>
                    <ToggleButton value="send">Send</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {/* A worked example of the exact settings chosen. Reading back what a
                    configuration will actually do is far more reliable than expecting
                    someone to infer it from three separate controls. */}
                <Box
                  sx={{
                    background: 'rgba(45,212,191,0.08)',
                    border: '1px solid rgba(45,212,191,0.28)',
                    borderRadius: '10px',
                    p: '10px 12px',
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: T.teal, lineHeight: 1.6 }}>
                    {scheduleSummary}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>
                  Invoices are raised for the period that has just finished — never one still in
                  progress. &quot;Draft only&quot; creates it for you to review and send by hand.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveBilling}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rangeFor}
        onClose={() => setRangeFor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate invoice — {rangeFor?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography sx={{ fontSize: 13, color: T.ink3 }}>
            Pick the period, then preview the numbers before anything is created or sent.
          </Typography>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={rangeFrom}
            onChange={e => {
              setRangeFrom(e.target.value);
              setRangePreview(null);
            }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={rangeTo}
            onChange={e => {
              setRangeTo(e.target.value);
              setRangePreview(null);
            }}
          />

          {!rangePreview && (
            <Button
              variant="outlined"
              disabled={!rangeFrom || !rangeTo || previewing}
              onClick={submitRangePreview}
              sx={{ alignSelf: 'flex-start' }}
            >
              {previewing ? 'Calculating…' : 'Preview'}
            </Button>
          )}

          {rangePreview && (
            <Box sx={{ border: `1px solid ${T.line}`, borderRadius: '10px', p: '14px' }}>
              {rangePreview.existing ? (
                <Typography sx={{ fontSize: 13, color: T.amber, mb: 1 }}>
                  Already invoiced for this exact range — {rangePreview.invoiceNumber} (
                  {rangePreview.status}). Showing its stored figures below.
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: T.ink3, mb: 1 }}>
                  {rangePreview.periodLabel} · proforma, not yet raised
                </Typography>
              )}
              {rangePreview.lines.map((line, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.ink, mb: '4px' }}>
                  <span>{line.description}</span>
                  <span>{money(line.amount, rangePreview.currency)}</span>
                </Box>
              ))}
              <Box sx={{ borderTop: `1px solid ${T.line}`, mt: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.ink2 }}>
                  <span>Subtotal</span>
                  <span>{money(rangePreview.subtotal, rangePreview.currency)}</span>
                </Box>
                {rangePreview.tax > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.ink2 }}>
                    <span>Tax</span>
                    <span>{money(rangePreview.tax, rangePreview.currency)}</span>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: T.ink }}>
                  <span>Total</span>
                  <span>{money(rangePreview.total, rangePreview.currency)}</span>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRangeFor(null)}>Cancel</Button>
          {rangePreview && !rangePreview.existing && (
            <>
              <Button
                disabled={generatingRange}
                onClick={() => confirmRangeGenerate(false)}
              >
                {generatingRange ? 'Saving…' : 'Save as draft'}
              </Button>
              <Button
                variant="contained"
                disabled={generatingRange}
                onClick={() => confirmRangeGenerate(true)}
              >
                {generatingRange ? 'Sending…' : 'Generate & send'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicRatesTable;
